import { createSlice } from '@reduxjs/toolkit'
import { matchById, isTrueStringMatch } from 'apps/builder/lib/utils'
import {
  orderByPosition,
  reIndexOnDelete,
  reIndexByMatch,
} from 'apps/builder/store/helpers'
import { isLoopScreen } from 'apps/builder/components/workspace/sheets/helpers'
import { lib, actions as clientActions } from '@gocanvas/client'
const {
  LABELS_ABOVE_SHEET_TYPE_ID, //11
  LOOP_SHEET_TYPE_ID, //15
  ADVANCED_LOOP_SHEET_TYPE_ID, //13
  LABELS_ABOVE_STYLE,
  TABLE_STYLE,
  DEFAULT_SHEET_TYPE_ID,
} = lib
const { loadFormData } = clientActions
import { deterministicGuid } from 'apps/builder/lib/hash'
import {
  importFormData,
  updateEntry,
  deleteEntry,
  addSheet,
  addTableSheet,
  addListSheet,
  updateSheet,
  updateSheetSection,
  deleteSheet,
  deleteChildSheet,
  deleteLoopSheet,
  copySheet,
  copyTableOrSimpleLoopSheet,
  splitSheet,
  editSheet,
  convertToLoopScreen,
  promoteToLoopScreen,
  convertToTableScreen,
  promoteToTableScreen,
  convertListToScreen,
  convertScreenToList,
} from 'apps/formsCreationKit/store/actions/sharedActions'

import { saveApp } from 'apps/formsCreationKit/store/reducers/settings/appSettings'

const filterDeletedSheets = (state, action) => {
  const { id, additionalSheets = [] } = action.payload
  const sheetsToDelete = [id, ...additionalSheets.map(({ id }) => id)]
  return state.filter(sheet => !sheetsToDelete.includes(sheet.id))
}

const convertToLoopSheet = (state, action) => {
  const {
    id,
    section_id,
    parentSheetId,
    parentSheetTypeId,
    newSheetId,
    newSectionId,
    newSheetDescription,
  } = action.payload
  const sheetIndex = state.findIndex(matchById(id))
  const sheet = state[sheetIndex]
  const isSimpleLoopSheet =
    parentSheetTypeId && parentSheetTypeId === LOOP_SHEET_TYPE_ID //15 vs 13

  // the keyfield will always end up on the new sheet w/ its multi_sheet pointing to
  // the new section... which will then hold the old sheet
  const newSheet = {
    id: newSheetId,
    description: newSheetDescription,
    section_id, // the new sheet remains still in old section
    sheet_type_id: LOOP_SHEET_TYPE_ID, // always 15
    multi_section: newSectionId,
    placeholder: `Type screen name`,
    position: sheet.position, // always retains the position of the original sheet
    pdf_visibility: 0,
    style: LABELS_ABOVE_STYLE,
  }
  // new section will hold the old sheet
  const oldSheet = {
    ...sheet,
    section_id: newSectionId, //old sheet goes in the new section
    sheet_type_id: LABELS_ABOVE_SHEET_TYPE_ID, //11
    position: 0, // will be now be the only sheet in the new section, to start
  }

  return [
    ...state.slice(0, sheetIndex),
    newSheet,
    oldSheet,
    // sheetIndex + 1 skips over the original sheet we'd replaced with oldSheet
    ...state.slice(sheetIndex + 1),
  ].map(sh => {
    // if the sheet already had a parent sheet (type 15)
    // convert that parent sheet to an advanced loop (13)
    if (isSimpleLoopSheet && isTrueStringMatch(sh.id, parentSheetId)) {
      return {
        ...sh,
        sheet_type_id: ADVANCED_LOOP_SHEET_TYPE_ID, //13
      }
    }
    return sh
  })
}

// this is the same as convertToLoopSheet, but with different sheet type ids
const convertToTableSheet = (state, action) => {
  const {
    id,
    section_id,
    parentSheetId,
    parentSheetTypeId,
    newSheetId,
    newSectionId,
    keyField,
    newSheetDescription,
  } = action.payload
  const sheetIndex = state.findIndex(matchById(id))
  const sheet = state[sheetIndex]
  const isSimpleLoopSheet = parentSheetTypeId === LOOP_SHEET_TYPE_ID //15 vs 13

  const newSheet = {
    id: newSheetId,
    description: newSheetDescription,
    section_id,
    sheet_type_id: LOOP_SHEET_TYPE_ID, //15
    style: TABLE_STYLE,
    multi_section: newSectionId,
    placeholder: `Type screen name`,
    pdf_visibility: 0,
    position: sheet.position,
  }
  const oldSheet = {
    ...sheet,
    section_id: newSectionId,
    sheet_type_id: LABELS_ABOVE_SHEET_TYPE_ID,
    position: 0, // will be now the only sheet in the section
  }

  return [
    ...state.slice(0, sheetIndex),
    newSheet,
    oldSheet,
    // sheetIndex + 1 filters out the original sheet we replaced with oldSheet
    ...state.slice(sheetIndex + 1),
  ].map(sh => {
    // if the sheet already had a parent sheet, convert the parent sheet to an advanced loop 13
    if (isSimpleLoopSheet && isTrueStringMatch(sh.id, parentSheetId)) {
      return {
        ...sh,
        sheet_type_id: ADVANCED_LOOP_SHEET_TYPE_ID, //13
      }
    }
    return sh
  })
}

const processCopyOrSplitSheet = (state, action) => {
  const { id, newId, sectionId, newSectionId, parentSheet } = action.payload
  const actionType = action.type
  const originalSheet = state.find(matchById(id))
  const copiedSheet = {
    ...originalSheet,
    id: newId,
    section_id: parentSheet ? sectionId : newSectionId,
    description: `${originalSheet.description || ''} - ${
      actionType === copySheet.type ? 'copy' : 'split'
    }`,
    position: parentSheet ? originalSheet.position + 1 : 0,
  }

  if (parentSheet) {
    const originalSheetIndex = state.findIndex(matchById(id))
    // Add copied sheet after the original sheet
    const newState = [
      ...state.slice(0, originalSheetIndex + 1),
      copiedSheet,
      ...state.slice(originalSheetIndex + 1),
    ]
    // Update positions of sheets in the same section to account for the copied sheet
    return newState.map(sheet => {
      if (
        sheet.id !== copiedSheet.id &&
        sheet.section_id === sectionId &&
        sheet.position >= copiedSheet.position
      ) {
        return {
          ...sheet,
          position: sheet.position + 1,
        }
      } else if (sheet.id === parentSheet.id) {
        // Convert parent screen from a simple loop to advanced loop when there are multiple children
        return {
          ...sheet,
          sheet_type_id: ADVANCED_LOOP_SHEET_TYPE_ID,
        }
      }
      return sheet
    })
  } else {
    return [...state, copiedSheet]
  }
}

const createTableSheets = (state, action) => {
  const { multiSectionGuid, multiSheetGuid, keyField, ...sheet } =
    action.payload

  const newTableSheets = [
    {
      ...sheet,
      multi_section: multiSectionGuid ? multiSectionGuid : null,
    },
  ]

  if (multiSectionGuid && multiSheetGuid) {
    newTableSheets.push({
      ...sheet,
      sheet_type_id: 11,
      style: 0,
      id: multiSheetGuid,
      section_id: multiSectionGuid,
    })
  }

  return [...state, ...newTableSheets]
}

const convertToScreen = (state, action) => {
  const { id } = action.payload
  return state
    .map(entry => {
      if (id !== entry.id) return entry
      return {
        ...entry,
        autoFocus: false,
        sheet_type_id: DEFAULT_SHEET_TYPE_ID,
      }
    })
    .sort(orderByPosition)
}

const convertToList = (state, action) => {
  const { id } = action.payload
  return state
    .map(sheet => {
      if (id !== sheet.id) return sheet
      return {
        ...sheet,
        autoFocus: false,
        sheet_type_id: LOOP_SHEET_TYPE_ID,
        style: 1,
        multi_section_id: null,
      }
    })
    .sort(orderByPosition)
}

export const initialState = []

export const sheetsSliceExtraReducers = builder => {
  builder
    .addCase(addSheet, (state, action) => {
      const { sectionPosition, ...sheetPayload } = action.payload
      state.push({
        ...sheetPayload,
      })
    })
    .addCase(addTableSheet, createTableSheets)
    .addCase(addListSheet, createTableSheets)
    .addCase(updateSheet, (state, action) => {
      const { id } = action.payload
      return state
        .map(entry => {
          if (id !== entry.id) return entry
          return {
            ...entry,
            autoFocus: false,
            ...action.payload,
          }
        })
        .sort(orderByPosition)
    })
    .addCase(updateSheetSection, (state, action) => {
      // need to handle sometimes converting parent sheet 13 to a 15 & vice versa depending on what happened
      const {
        currentSectionId,
        deleteSection,
        parent_sheet_id,
        childSheets,
        standardPrimarySheets,
        ...cleanedPayload
      } = action.payload
      const { id, section_id, position } = cleanedPayload

      const sheet = state.find(matchById(id)) // the sheet being moved
      const newSheet = {
        ...sheet,
        autoFocus: false,
        ...cleanedPayload,
      }

      // all sheets in the section the sheet is being moved to
      const toSectionSheets = state.filter(
        sheet => sheet.section_id === section_id
      )

      // the parent sheet, if any, of the section the sheet is being moved to
      const toParentSheet = state.find(
        sheet => sheet.multi_section === section_id
      )

      // all sheets in the section the sheet is being moved from
      const fromSectionSheets = state.filter(
        sheet => sheet.section_id === currentSectionId
      )

      // the parent sheet, if any, of the section the sheet is being moved from
      const fromParentSheet = state.find(
        sheet => sheet.multi_section === currentSectionId
      )

      // sheet was moved within the same section
      const sameSection = sheet.section_id === section_id
      const maxIndex = sameSection
        ? toSectionSheets.length
        : toSectionSheets.length + 1

      const newPosition = Math.min(position, maxIndex)

      // handle new sheet position within the sheet's new section, or within the sameSection
      const reIndexedSectionSheets = sameSection
        ? reIndexByMatch(
            toSectionSheets,
            matchById(id),
            newPosition,
            sheet.position
          )
        : [
            ...toSectionSheets.slice(0, newPosition),
            newSheet,
            ...toSectionSheets.slice(newPosition).map(sheet => ({
              ...sheet,
              position: sheet.position + 1,
            })),
          ]

      if (!sameSection) {
        reIndexedSectionSheets.push(
          ...reIndexOnDelete(fromSectionSheets, sheet.position, id)
        )
      }

      // handle updating sheet positions in previous section, if not sameSection ???

      const newState = state.map(entry => {
        const affectedSheet = reIndexedSectionSheets.find(matchById(entry.id))

        // changes to toParentSheet
        if (toParentSheet && isTrueStringMatch(entry.id, toParentSheet.id)) {
          // if section sheets was 1 and we're adding one, parent becomes advanced
          if (toSectionSheets.length === 1) {
            return {
              ...entry,
              position: affectedSheet?.position || entry.position,
              sheet_type_id: 13,
            }
          }
        }

        // changes to the fromParentSheet
        if (
          fromParentSheet &&
          isTrueStringMatch(entry.id, fromParentSheet.id)
        ) {
          // if section sheets was 2 and we're removing one, the parent becomes a simple list
          if (fromSectionSheets.length === 2) {
            return {
              ...entry,
              position: affectedSheet?.position || entry.position,
              sheet_type_id: 15,
            }
          }
          // if there are no more sheets in this section, the parent sheet becomes a normal sheet
          if (fromSectionSheets.length == 1) {
            return {
              ...entry,
              position: affectedSheet?.position || entry.position,
              multi_section: null,
              allow_duplicate: null,
              display_entry: null,
              sheet_type_id: 11,
            }
          }
        }

        // not an affected sheet
        if (!affectedSheet) return entry

        // affected sheet that's not the one being moved
        if (affectedSheet.id !== id) {
          return affectedSheet
        }

        // affected sheet that was moved
        return {
          ...entry,
          autoFocus: false,
          ...cleanedPayload,
        }
      })
      return newState.sort(orderByPosition)
    })
    .addCase(deleteSheet, filterDeletedSheets)
    .addCase(deleteChildSheet, (state, action) => {
      const { deleteTopLevelSection, parentSheet } = action.payload
      const newState = filterDeletedSheets(state, action)
      const parentSheetChildren = newState.filter(
        sheet => sheet.section_id === parentSheet.multi_section
      )

      return newState.map(sheet => {
        if (sheet.id === parentSheet.id) {
          if (deleteTopLevelSection) {
            // Convert parent screen to a normal screen when the last/only child screen is deleted
            return {
              ...sheet,
              sheet_type_id: LABELS_ABOVE_SHEET_TYPE_ID,
              multi_section: null,
              allow_duplicate: null,
              display_entry: null,
            }
          } else if (
            parentSheetChildren.length === 1 &&
            !isLoopScreen(parentSheetChildren[0])
          ) {
            // Convert parent screen from an advanced loop to a standard loop when there's only 1 child that's not a loop
            return {
              ...sheet,
              sheet_type_id: LOOP_SHEET_TYPE_ID,
            }
          }
        }
        return sheet
      })
    })
    .addCase(deleteLoopSheet, (state, action) => {
      const { id, sectionId, parentSheet, siblings, directDescendants } =
        action.payload
      const deletedLoopSheet = state.find(matchById(id))
      const deletedLoopSheetPosition = deletedLoopSheet.position
      // When the deleted loop sheet is nested, move the child sheets to the deleted
      // loop sheet section to prevent reordering of the parent screen sections
      const updatedChildSheets = directDescendants.map((sheet, index) => ({
        ...sheet,
        section_id: parentSheet ? sectionId : sheet.newSectionId,
        position: parentSheet ? deletedLoopSheetPosition + index : 0,
      }))

      const childSheetIds = directDescendants.map(({ id }) => id)

      let newState = state.filter(
        sheet => sheet.id !== id && !childSheetIds.includes(sheet.id)
      )

      if (
        parentSheet &&
        !siblings.length &&
        directDescendants.length === 1 &&
        !isLoopScreen(directDescendants[0])
      ) {
        // Convert parent screen from an advanced loop to a standard loop when there's only 1 child that's not a loop
        newState = newState.map(sheet => ({
          ...sheet,
          sheet_type_id:
            sheet.id === parentSheet.id
              ? LOOP_SHEET_TYPE_ID
              : sheet.sheet_type_id,
        }))
      }

      if (parentSheet && siblings.length) {
        // Insert the child sheets into the same position in the array as the deleted loop sheet
        const insertionIndex = newState.findIndex(
          sheet =>
            sheet.section_id === sectionId &&
            sheet.position > deletedLoopSheetPosition
        )
        // If no sheet with a higher position is found, insert the sheet the end of the array
        const indexToInsert =
          insertionIndex === -1 ? newState.length : insertionIndex

        return [
          ...newState.slice(0, indexToInsert),
          ...updatedChildSheets,
          ...newState.slice(indexToInsert),
        ]
      } else {
        return [...newState, ...updatedChildSheets]
      }
    })
    .addCase(copySheet, processCopyOrSplitSheet)
    .addCase(copyTableOrSimpleLoopSheet, (state, action) => {
      const {
        sheetsToCopy,
        newMultiSectionGuid,
        newSectionGuid,
        newSheetGuid,
        newMultiSheetGuid,
        seed,
      } = action.payload
      const originalSheets = state.filter(({ id }) => sheetsToCopy.includes(id))

      const newSheets = originalSheets.map(sheet => {
        const isMultiSection = sheet.sheet_type_id === 15
        const displayEntryGuid = sheet?.display_entry?.guid
        return {
          ...sheet,
          display_entry: displayEntryGuid
            ? { guid: deterministicGuid(displayEntryGuid, seed) }
            : null,
          id: isMultiSection ? newSheetGuid : newMultiSheetGuid,
          section_id: isMultiSection ? newSectionGuid : newMultiSectionGuid,
          multi_section: isMultiSection ? newMultiSectionGuid : null,
          description: `${sheet.description || ''} - copy`,
        }
      })

      return [...state, ...newSheets]
    })
    .addCase(splitSheet, processCopyOrSplitSheet)
    .addCase(editSheet, (state, action) => {
      const { id, additionalSheetIds = [], sheetPayload } = action.payload
      const sheetsToUpdate = [id, ...additionalSheetIds]
      return state
        .map(sheet => {
          if (sheetsToUpdate.includes(sheet.id)) {
            return {
              ...sheet,
              ...sheetPayload,
            }
          }
          return sheet
        })
        .sort(orderByPosition)
    })
    .addCase(convertToLoopScreen, convertToLoopSheet)
    .addCase(promoteToLoopScreen, convertToLoopSheet)
    .addCase(convertToTableScreen, convertToTableSheet)
    .addCase(promoteToTableScreen, convertToTableSheet)
    .addCase(convertListToScreen, convertToScreen)
    .addCase(convertScreenToList, convertToList)
    .addCase(updateEntry, (state, action) => {
      const { sheetPayload } = action.payload
      if (sheetPayload) {
        const { sheet_id, style } = sheetPayload
        return state.map(sheet => {
          if (isTrueStringMatch(sheet.id, sheet_id)) {
            return {
              ...sheet,
              style,
            }
          }
          return sheet
        })
      }
      return state
    })
    .addCase(deleteEntry, (state, action) => {
      const { guid } = action.payload
      return state.map(sheet => {
        const { display_entry } = sheet
        if (display_entry && display_entry?.guid === guid) {
          return {
            ...sheet,
            display_entry: null,
          }
        }
        return sheet
      })
    })
    .addCase(loadFormData, (_, action) => {
      const { sheets } = action.payload
      return sheets
    })
    .addCase(importFormData, (state, action) => {
      const { sheets } = action.payload
      return [...state, ...sheets]
    })
    .addCase(saveApp.fulfilled, (state, action) => {
      const { entities } = action.payload
      const { sheets } = entities
      return sheets
    })
}

export const sheetsSliceConfig = {
  name: 'sheets',
  initialState: initialState,
  reducers: {},
  extraReducers: sheetsSliceExtraReducers,
}

export const sheetsSlice = createSlice(sheetsSliceConfig)
export const { actions: sheetsActions, reducer: sheetsReducer } = sheetsSlice
